import { Injectable, inject, PLATFORM_ID, NgZone } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';

// OpenTelemetry core
import { trace, context, Span, SpanStatusCode } from '@opentelemetry/api';
import {
  WebTracerProvider,
  SimpleSpanProcessor,
  BatchSpanProcessor,
} from '@opentelemetry/sdk-trace-web';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { W3CTraceContextPropagator } from '@opentelemetry/core';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION,
  ATTR_DEPLOYMENT_ENVIRONMENT_NAME,
} from '@opentelemetry/semantic-conventions';

// Auto-instrumentation
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch';
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { registerInstrumentations } from '@opentelemetry/instrumentation';

import { environment } from '../../../environments/environment';
import { TraceService } from './trace.service';
import { LoggerService } from './logger.service';

/**
 * Bootstraps the OpenTelemetry Web SDK for distributed tracing.
 *
 * Architecture:
 *
 *   Browser (this service)
 *     │  Creates spans, injects traceparent into fetch/XHR
 *     │
 *     ▼
 *   OTel Collector (otlpEndpoint)
 *     │  Receives spans via OTLP/HTTP
 *     │
 *     ▼
 *   Grafana Tempo
 *     │  Stores & indexes traces
 *     │
 *     ▼
 *   Grafana Explore
 *        Full waterfall trace: Browser → API → DB
 *
 * Log correlation:
 *   Every log entry carries traceId & spanId, so in Grafana you can
 *   click "View trace" directly from a Loki log line.
 */
@Injectable({ providedIn: 'root' })
export class OpenTelemetryService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly isBrowser = isPlatformBrowser(this.platformId);
  private readonly ngZone = inject(NgZone);
  private readonly router = inject(Router);
  private readonly traceService = inject(TraceService);
  private readonly logger = inject(LoggerService);

  private tracerProvider: WebTracerProvider | null = null;

  /**
   * Initialise the OpenTelemetry SDK.
   * Call once from bootstrap after LoggerService.init().
   */
  init(): void {
    if (!this.isBrowser) {
      this.logger.info('OpenTelemetry', 'Skipping OTel init – not in browser');
      return;
    }

    const tracingConfig = (environment as any).tracing;
    if (!tracingConfig?.enabled) {
      this.logger.info('OpenTelemetry', 'Tracing disabled in environment config');
      return;
    }

    const otlpEndpoint: string | undefined = tracingConfig.otlpEndpoint;
    if (!otlpEndpoint) {
      this.logger.warn('OpenTelemetry', 'No OTLP endpoint configured – traces will not be exported');
      return;
    }

    this.logger.info('OpenTelemetry', 'Initialising OpenTelemetry SDK', {
      otlpEndpoint,
      serviceName: tracingConfig.serviceName ?? 'dhe-frontend',
    });

    // ---- Resource (identifies this service in Tempo) -----------------------
    const resource = resourceFromAttributes({
      [ATTR_SERVICE_NAME]: tracingConfig.serviceName ?? 'dhe-frontend',
      [ATTR_SERVICE_VERSION]: environment.version,
      [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: environment.environmentName,
    });

    // ---- Exporter (ships spans to OTel Collector → Tempo) ------------------
    const exporterUrl = otlpEndpoint.endsWith('/v1/traces')
      ? otlpEndpoint
      : `${otlpEndpoint}/v1/traces`;

    const exporter = new OTLPTraceExporter({
      url: exporterUrl,
      headers: tracingConfig.otlpHeaders ?? {},
    });

    // ---- Span processor (Simple for dev/QA, Batch for production) ----------
    const spanProcessor = environment.production
      ? new BatchSpanProcessor(exporter)
      : new SimpleSpanProcessor(exporter);

    // ---- Tracer Provider ---------------------------------------------------
    this.tracerProvider = new WebTracerProvider({
      resource,
      spanProcessors: [spanProcessor],
    });

    this.tracerProvider.register({
      contextManager: new ZoneContextManager(),
      propagator: new W3CTraceContextPropagator(),
    });

    // ---- Auto-instrumentation (fetch & XHR) --------------------------------
    registerInstrumentations({
      instrumentations: [
        new FetchInstrumentation({
          // Don't trace calls to observability infra
          ignoreUrls: [/\/__logs/, /\/__traces/],
          propagateTraceHeaderCorsUrls: [/.*/],
        }),
        new XMLHttpRequestInstrumentation({
          ignoreUrls: [/\/__logs/, /\/__traces/],
          propagateTraceHeaderCorsUrls: [/.*/],
        }),
      ],
    });

    // ---- Route navigation spans --------------------------------------------
    this.setupRouteSpans();

    // ---- Wire up TraceService → OTel bridge -------------------------------
    // When TraceService finishes a span, create a matching OTel span
    this.traceService.onSpanFinished((customSpan) => {
      this.exportCustomSpanToOTel(customSpan);
    });

    // ---- Log that tracing is active ---------------------------------------
    const activeSpan = trace.getActiveSpan();
    const spanContext = activeSpan?.spanContext();
    this.logger.info('OpenTelemetry', 'OpenTelemetry SDK initialised', {
      traceId: spanContext?.traceId,
      spanId: spanContext?.spanId,
    });
  }

  /**
   * Create spans for Angular router navigation events so that each
   * page navigation appears in the trace timeline.
   */
  private setupRouteSpans(): void {
    let currentNavSpan: Span | null = null;

    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        // End previous navigation span
        if (currentNavSpan) {
          currentNavSpan.setStatus({ code: SpanStatusCode.OK });
          currentNavSpan.end();
        }

        // Start a new span for this route (runs outside Angular zone)
        this.ngZone.runOutsideAngular(() => {
          const tracer = trace.getTracer('dhe-router');
          currentNavSpan = tracer.startSpan(`Route: ${event.urlAfterRedirects}`, {
            attributes: {
              'page.url': event.urlAfterRedirects,
              'page.navigation_id': event.id,
            },
          });
        });
      });
  }

  /**
   * Bridge: when the lightweight TraceService finishes a span,
   * create a corresponding OTel span so it appears in Tempo.
   */
  private exportCustomSpanToOTel(customSpan: import('./trace.service').TraceSpan): void {
    const tracer = trace.getTracer('dhe-custom');

    const otelSpan = tracer.startSpan(
      customSpan.name,
      {
        attributes: {
          ...customSpan.attributes,
          'span.kind': customSpan.kind,
        },
        startTime: customSpan.startTime,
      },
      context.active(),
    );

    // Replay events
    for (const evt of customSpan.events) {
      otelSpan.addEvent(evt.name, evt.attributes, evt.timestamp);
    }

    // Set status
    if (customSpan.status === 'error') {
      otelSpan.setStatus({ code: SpanStatusCode.ERROR });
    } else {
      otelSpan.setStatus({ code: SpanStatusCode.OK });
    }

    otelSpan.end(customSpan.endTime);
  }

  /** Clean shutdown – flush pending spans. */
  shutdown(): Promise<void> {
    if (this.tracerProvider) {
      return this.tracerProvider.shutdown();
    }
    return Promise.resolve();
  }
}
